import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import analyticsService from '../../services/AnalyticsService';
import dashboardService from '../../services/DashboardService.js';
import { TrendingUp, TrendingDown, Package, Truck, Users, AlertCircle, IndianRupee, ArrowRight, ShoppingCart, FileText, Network } from 'lucide-react';
import isExternalNetworkEnabled, {
  hasExternalCommissionAccess,
  hasMlAgentCommissionAccess,
} from '../../utils/networkMode.js';
import { useTranslation } from 'react-i18next';
import {
  AnalyticsStatCard,
  analyticsStatsGrid4Class,
  analyticsStatsGrid3Class,
  analyticsIconChipClass,
  buildAnalyticsParams as buildSharedAnalyticsParams,
  getStoredAnalyticsCompany,
  setStoredAnalyticsCompany,
  isSystemAdminUser,
} from './common/index.js';
import { formatCurrencyPaise } from '../../utils/formatCurrency.js';
const formatCurrency = (v) => formatCurrencyPaise(v, '₹', { alwaysPaise: true });


const DEFAULT_AGENT_NETWORK_SUMMARY = {
  partnerships_accepted: 0,
  partnerships_pending: 0,
  partnerships_total: 0,
  unique_partner_agents: 0,
  external_shipments: 0,
  external_shipment_freight: 0,
};

const Analytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [profitLossData, setProfitLossData] = useState(null);
  const [onContractData, setOnContractData] = useState(null);
  const [vehicleContractData, setVehicleContractData] = useState(null);
  const [agentNetworkData, setAgentNetworkData] = useState(null);
  const [agentNetworkLoading, setAgentNetworkLoading] = useState(false);
  const agentNetworkFetchId = useRef(0);
  const [filter, setFilter] = useState('current_month');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  
  // New state for company filtering
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(getStoredAnalyticsCompany);

  // Fetch current user and companies on mount
  useEffect(() => {
    fetchUserAndCompanies();
  }, []);

  // Block access when the company has Analytical Reports disabled (System Admin always allowed)
  useEffect(() => {
    if (!currentUser) return;
    const role = currentUser.role_name || currentUser.role;
    if (role === 'System Admin') return;
    const ca = currentUser.company_features || currentUser.company_can_access || {};
    if (ca.analytical_reports === false) {
      navigate('/', { replace: true });
    }
  }, [currentUser, navigate]);

  // Fetch analytics when filter or company changes
  useEffect(() => {
    if (currentUser) {
      fetchAnalytics();
    }
  }, [filter, selectedCompany, currentUser]);

  const fetchUserAndCompanies = async () => {
    try {
      // Get current user from localStorage or API
      const user = JSON.parse(sessionStorage.getItem('user'));
      setCurrentUser(user);

      // Only fetch companies list for System Admin
      if (user && isSystemAdminUser(user)) {
        const companiesResponse = await dashboardService.getAllCompanies();
        setCompanies(companiesResponse.data || companiesResponse.companies || []);
        const stored = getStoredAnalyticsCompany();
        if (stored && stored !== 'all') {
          setSelectedCompany(stored);
        }
      } else if (user) {
        // For Admin/Accountant, set selected company to their own company
        const own = user.company_internal_id || user.company_id;
        setSelectedCompany(own);
        setStoredAnalyticsCompany(own);
      }
    } catch (error) {
      console.error('Error fetching user and companies:', error);
    }
  };

  const buildAnalyticsParams = useCallback(() => {
    return buildSharedAnalyticsParams({
      filter,
      customDates,
      selectedCompany,
      currentUser,
    });
  }, [filter, customDates, selectedCompany, currentUser]);

  const shouldFetchAgentNetwork = useCallback(() => {
    if (!currentUser) return false;
    const role = currentUser.role_name || currentUser.role;
    const isSysAdmin = role === 'System Admin';
    const isAdminOrAccountant = role === 'Admin' || role === 'Accountant' || role === 'Hub Admin';
    return isSysAdmin || (isAdminOrAccountant && isExternalNetworkEnabled(currentUser));
  }, [currentUser]);

  const fetchAgentNetworkPreview = useCallback(async () => {
    if (!shouldFetchAgentNetwork()) {
      setAgentNetworkData(null);
      return;
    }

    const fetchId = ++agentNetworkFetchId.current;
    setAgentNetworkLoading(true);
    try {
      const result = await analyticsService.getAgentNetworkAnalytics(buildAnalyticsParams());
      if (fetchId !== agentNetworkFetchId.current) return;
      setAgentNetworkData(result);
    } catch (error) {
      if (fetchId !== agentNetworkFetchId.current) return;
      console.error('Agent network preview fetch failed:', error);
      setAgentNetworkData({
        enabled: false,
        loadError: true,
        summary: null,
        message:
          error.response?.data?.error ||
          'Could not load agent network preview. Open the full report or refresh.',
      });
    } finally {
      if (fetchId === agentNetworkFetchId.current) {
        setAgentNetworkLoading(false);
      }
    }
  }, [buildAnalyticsParams, shouldFetchAgentNetwork]);

  // Agent network preview — fetched separately so heavy dashboard calls cannot block or overwrite it
  useEffect(() => {
    if (currentUser) {
      fetchAgentNetworkPreview();
    }
  }, [currentUser, fetchAgentNetworkPreview]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = buildAnalyticsParams();

      const role = currentUser?.role_name || currentUser?.role;
      const isAgentRole = role === 'Agent';
      const isSysAdminRole = role === 'System Admin';
      const ca = currentUser?.company_features || currentUser?.company_can_access || {};
      const canFetchOnContract = !isAgentRole && (isSysAdminRole || Boolean(ca.on_contract));
      const canFetchVehicleContract = !isAgentRole && (isSysAdminRole || Boolean(ca.on_contract_truck));

      const settled = await Promise.allSettled([
        analyticsService.getDashboardSummary(params),
        isAgentRole
          ? Promise.resolve(null)
          : analyticsService.getProfitLoss(params),
        canFetchOnContract
          ? analyticsService.getOnContractAnalytics(params)
          : Promise.resolve(null),
        canFetchVehicleContract
          ? analyticsService.getVehicleContractAnalytics(params)
          : Promise.resolve(null),
      ]);

      const pick = (idx) => (settled[idx].status === 'fulfilled' ? settled[idx].value : null);

      setDashboardData(pick(0));
      setProfitLossData(pick(1));
      setOnContractData(pick(2));
      setVehicleContractData(pick(3));
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setStoredAnalyticsCompany(companyId);
  };

  const StatCard = AnalyticsStatCard;

  const ModuleCard = ({ title, description, icon: Icon, onClick }) => (
    <div
      className="relative bg-white rounded-2xl border border-slate-100 p-3 md:p-4 lg:p-5 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer overflow-hidden"
      onClick={onClick}
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-blue-200" />
      <div className="flex items-start gap-3 md:hidden">
        <div className={`p-2 rounded-xl shrink-0 border ${analyticsIconChipClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 leading-tight">{title}</h3>
          <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-snug">{description}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" aria-hidden="true" />
      </div>

      <div className="hidden md:block">
        <div className="flex items-center mb-3">
          <div className={`p-2.5 rounded-xl border mr-3 ${analyticsIconChipClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4 leading-relaxed">{description}</p>
        <div className="flex items-center text-blue-600 text-sm font-medium">
          <span>View Analytics</span>
          <ArrowRight className="w-4 h-4 ml-1" />
        </div>
      </div>
    </div>
  );

  const role = currentUser?.role_name || currentUser?.role;
  const isSysAdmin = role === 'System Admin';
  const isAgent = role === 'Agent';
  const isAdminOrAccountant = role === 'Admin' || role === 'Accountant' || role === 'Hub Admin';
  const isExternal = isExternalNetworkEnabled(currentUser);
  // Same can_access gating as Reports.js — only show modules enabled for the company
  const companyAccess = currentUser?.company_features || currentUser?.company_can_access || {};
  const hasShipmentAccess = isSysAdmin || Boolean(companyAccess.shipment);
  const hasExpensesAccess = isSysAdmin || Boolean(companyAccess.expenses);
  const hasOnContractAccess = isSysAdmin || Boolean(companyAccess.on_contract);
  const hasOnContractTruckAccess = isSysAdmin || Boolean(companyAccess.on_contract_truck);
  const hasCommissionAccess =
    hasMlAgentCommissionAccess(currentUser) || hasExternalCommissionAccess(currentUser);
  const showAgentNetworkTop =
    isSysAdmin || (isAdminOrAccountant && isExternal);
  const showCompanyOverviewTop =
    isAgent ||
    isAdminOrAccountant ||
    (isSysAdmin && selectedCompany && selectedCompany !== 'all');
  const companyOverview = dashboardData?.company_overview || {};
  const agentSummary = {
    ...DEFAULT_AGENT_NETWORK_SUMMARY,
    ...(agentNetworkData?.summary || {}),
  };
  const agentNetworkEnabled = agentNetworkData?.enabled !== false && !agentNetworkData?.loadError;
  const hasAgentPreviewData =
    agentNetworkEnabled && agentNetworkData?.summary && !agentNetworkData.loadError;
  const alerts = dashboardData?.alerts;
  const hasActiveAlerts = Boolean(
    alerts && (
      (alerts.pending_salary_count ?? 0) > 0 ||
      (alerts.pending_expense_count ?? 0) > 0 ||
      (alerts.missing_shipments ?? 0) > 0 ||
      (alerts.returned_shipments ?? 0) > 0 ||
      (alerts.inventory_alerts?.out_of_stock ?? 0) > 0 ||
      (alerts.inventory_alerts?.low_stock ?? 0) > 0
    )
  );

  if (loading) {
    return (
      <DashboardLayout>
        <Loader fullScreen={false} size="default" message="Loading Analytics Dashboard..." />
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header with Company Filter and Date Filter */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-semibold text-secondary">Analytics Dashboard</h1>
                    {isSysAdmin && selectedCompany && selectedCompany !== 'all' && (
                      <p className="text-sm text-slate-500 mt-1">
                        Showing data for: {companies.find(c => c.id === parseInt(selectedCompany, 10))?.company_name}
                      </p>
                    )}
                  </div>
                  <DateFilterDropdown
                    filter={filter}
                    setFilter={setFilter}
                    customDates={customDates}
                    setCustomDates={setCustomDates}
                    onApply={() => {
                      fetchAnalytics();
                      fetchAgentNetworkPreview();
                    }}
                    dateRange={profitLossData?.filter}
                    currentUser={currentUser}
                    companies={companies}
                    selectedCompany={selectedCompany}
                    onCompanyChange={handleCompanyChange}
                    showCompanyFilter={true}
                  />
                </div>

                {/* Company-specific overview — Admin, Accountant, Agent (and SysAdmin when filtered to one company) */}
                {showCompanyOverviewTop && dashboardData?.shipments && (
                  <div className="mb-6">
                    <div className={`grid grid-cols-2 ${isAgent ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-4`}>
                      {isAgent ? (
                        <>
                          <StatCard
                            title="My shipments"
                            value={dashboardData.shipments.total_count}
                            subtitle={`Revenue: ${formatCurrency(dashboardData.shipments.total_revenue)}`}
                            icon={Package}
                            color="bg-blue-100 text-blue-600 border-blue-200"
                            onClick={() => navigate('/analytics/shipments')}
                          />
                          <StatCard
                            title="Outstanding collections"
                            value={formatCurrency(dashboardData.shipments.pending)}
                            subtitle={`Collected: ${formatCurrency(dashboardData.shipments.paid)}`}
                            icon={IndianRupee}
                            color="bg-orange-100 text-orange-600 border-orange-200"
                          />
                          <StatCard
                            title="Dispatched"
                            value={companyOverview.dispatched_shipments ?? 0}
                            subtitle={`Delivered: ${companyOverview.delivered_shipments ?? 0}`}
                            icon={Truck}
                            color="bg-indigo-100 text-indigo-600 border-indigo-200"
                            onClick={() => navigate('/analytics/shipments')}
                          />
                          <StatCard
                            title="Missing & returned"
                            value={(dashboardData.alerts?.missing_shipments ?? 0) + (dashboardData.alerts?.returned_shipments ?? 0)}
                            subtitle={`${dashboardData.alerts?.missing_shipments ?? 0} missing · ${dashboardData.alerts?.returned_shipments ?? 0} returned`}
                            icon={AlertCircle}
                            color="bg-rose-100 text-rose-600 border-rose-200"
                            onClick={() => navigate('/analytics/missing-returned')}
                          />
                        </>
                      ) : (
                        <>
                          <StatCard
                            title="Shipments"
                            value={dashboardData.shipments.total_count}
                            subtitle={`Revenue: ${formatCurrency(dashboardData.shipments.total_revenue)}`}
                            icon={Package}
                            color="bg-blue-100 text-blue-600 border-blue-200"
                            onClick={() => navigate('/analytics/shipments')}
                          />
                          <StatCard
                            title="Total Revenue"
                            value={formatCurrency(profitLossData?.summary?.total_revenue)}
                            subtitle={`Collected: ${formatCurrency(profitLossData?.summary?.collected_revenue ?? dashboardData.shipments.paid)}`}
                            icon={IndianRupee}
                            color="bg-emerald-100 text-emerald-600 border-emerald-200"
                          />
                          <StatCard
                            title="Total Expenses"
                            value={formatCurrency(profitLossData?.summary?.total_expenses)}
                            subtitle="Total expenditure"
                            icon={IndianRupee}
                            color="bg-rose-100 text-rose-600 border-rose-200"
                          />
                          <StatCard
                            title="Net Profit/Loss"
                            value={formatCurrency(profitLossData?.summary?.net_profit_loss)}
                            subtitle={
                              profitLossData?.summary
                                ? `${profitLossData.summary.status === 'profit' ? 'Profit' : 'Loss'} · Margin ${profitLossData.summary.profit_margin_percentage.toFixed(2)}%`
                                : 'Profit margin'
                            }
                            icon={profitLossData?.summary?.status === 'loss' ? TrendingDown : TrendingUp}
                            color={profitLossData?.summary?.status === 'loss' ? 'bg-rose-100 text-rose-600 border-rose-200' : 'bg-emerald-100 text-emerald-600 border-emerald-200'}
                          />
                          <StatCard
                            title="Outstanding"
                            value={formatCurrency(dashboardData.shipments.pending)}
                            subtitle="Amount to be collected"
                            icon={AlertCircle}
                            color="bg-orange-100 text-orange-600 border-orange-200"
                          />
                        </>
                      )}
                    </div>
                    {!isAgent && (companyOverview.external_shipments ?? 0) > 0 && (
                      <p className="text-sm text-blue-700 mt-3">
                        External network shipments in period: {companyOverview.external_shipments}
                        {isExternal ? ' — see Agent Network below for partnership details.' : '.'}
                      </p>
                    )}
                  </div>
                )}

                {/* Agent Network — compact preview only (full report on /analytics/agent-network) */}
                {showAgentNetworkTop && (
                  <div className="mb-6 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-sky-50 p-4 lg:p-5 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Network className="w-5 h-5 text-blue-600 shrink-0" />
                          <h2 className="text-lg font-bold text-gray-800">Agent Network</h2>
                          <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                            Preview
                          </span>
                        </div>
                        {!agentNetworkEnabled && agentNetworkData?.message ? (
                          <p className="text-sm text-amber-800">{agentNetworkData.message}</p>
                        ) : agentNetworkLoading ? (
                          <p className="text-sm text-gray-500">Loading network summary…</p>
                        ) : hasAgentPreviewData ? (
                          <>
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold">{agentSummary.partnerships_accepted}</span> active partnerships
                              {' · '}
                              <span className="font-semibold">{agentSummary.external_shipments}</span> external shipments
                              {' · '}
                              <span className="font-semibold">{agentSummary.unique_partner_agents}</span> partner agents
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {agentSummary.partnerships_total} partnership requests
                              {agentSummary.partnerships_pending > 0
                                ? ` · ${agentSummary.partnerships_pending} pending approval`
                                : ''}
                              {agentSummary.external_shipment_freight > 0
                                ? ` · ${formatCurrency(agentSummary.external_shipment_freight)} external freight`
                                : ''}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">No agent network data for this period.</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/analytics/agent-network')}
                        className="px-4 py-2.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 whitespace-nowrap shrink-0"
                      >
                        Open full report →
                      </button>
                    </div>
                  </div>
                )}

                {/* Profit/Loss Overview — System Admin all-companies view only (Admin/Accountant see it merged in Company Overview) */}
                {!isAgent && !showCompanyOverviewTop && profitLossData?.summary && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard
                      title="Total Revenue"
                      value={formatCurrency(profitLossData.summary.total_revenue)}
                      subtitle="Total income generated"
                      icon={IndianRupee}
                      color="bg-emerald-100 text-emerald-600 border-emerald-200"
                    />
                    <StatCard
                      title="Total Expenses"
                      value={formatCurrency(profitLossData.summary.total_expenses)}
                      subtitle="Total expenditure"
                      icon={IndianRupee}
                      color="bg-rose-100 text-rose-600 border-rose-200"
                    />
                    <StatCard
                      title="Net Profit/Loss"
                      value={formatCurrency(profitLossData.summary.net_profit_loss)}
                      subtitle={`${profitLossData.summary.status === 'profit' ? 'Profit' : 'Loss'}`}
                      icon={profitLossData.summary.status === 'profit' ? TrendingUp : TrendingDown}
                      color={profitLossData.summary.status === 'profit' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}
                    />
                    <StatCard
                      title="Profit Margin"
                      value={`${profitLossData.summary.profit_margin_percentage.toFixed(2)}%`}
                      subtitle="Revenue to profit ratio"
                      icon={TrendingUp}
                      color="bg-purple-100 text-purple-600 border-purple-200"
                    />
                  </div>
                )}

                {/* Legacy quick stats — System Admin viewing all companies (no company filter) */}
                {isSysAdmin && (!selectedCompany || selectedCompany === 'all') && dashboardData?.shipments && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <StatCard
                      title="Total Shipments"
                      value={dashboardData.shipments.total_count}
                      subtitle={`Revenue: ${formatCurrency(dashboardData.shipments.total_revenue)}`}
                      icon={Package}
                      color="bg-blue-100 text-blue-600 border-blue-200"
                      onClick={() => navigate('/analytics/shipments')}
                    />
                    <StatCard
                      title="Active Users"
                      value={dashboardData.resources?.active_users ?? 0}
                      subtitle="Currently active"
                      icon={Users}
                      color="bg-indigo-100 text-indigo-600 border-indigo-200"
                      onClick={() => navigate('/analytics/user-performance')}
                    />
                    <StatCard
                      title="Active Vehicles"
                      value={dashboardData.resources?.active_vehicles ?? 0}
                      subtitle="Fleet on duty"
                      icon={Truck}
                      color="bg-amber-100 text-amber-600 border-amber-200"
                      onClick={() => navigate('/analytics/vehicles')}
                    />
                    <StatCard
                      title="Outstanding Collections"
                      value={formatCurrency(dashboardData.shipments.pending)}
                      subtitle="To be collected"
                      icon={AlertCircle}
                      color="bg-orange-100 text-orange-600 border-orange-200"
                    />
                  </div>
                )}

                {/* Contract Summary Cards — gated by company can_access */}
                {!isAgent && ((hasOnContractAccess && onContractData?.summary) || (hasOnContractTruckAccess && vehicleContractData?.summary)) && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* On Contracts Summary */}
                    {hasOnContractAccess && onContractData?.summary && (
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <div className="p-2.5 rounded-xl bg-cyan-100 text-cyan-600 border border-cyan-200 mr-3">
                              <FileText className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">On Contracts</h3>
                              <p className="text-sm text-gray-600">Period summary</p>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate('/on-contracts/analytics')}
                            className="px-3 py-1 text-sm bg-cyan-50 text-cyan-600 rounded hover:bg-cyan-100 transition-colors"
                          >
                            View Details
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Total Contracts</span>
                            <span className="font-bold text-gray-800">{onContractData.summary.total_contracts ?? 0}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Revenue</span>
                            <span className="font-bold text-green-600">{formatCurrency(onContractData.summary.total_revenue)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Balance</span>
                            <span className="font-bold text-orange-600">{formatCurrency(onContractData.summary.total_balance)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Vehicle Contracts Summary */}
                    {hasOnContractTruckAccess && vehicleContractData?.summary && (
                      <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <div className="p-2.5 rounded-xl bg-purple-100 text-purple-600 border border-purple-200 mr-3">
                              <Truck className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">{t('pages.vehicleContract.title')}</h3>
                              <p className="text-sm text-gray-600">Period summary</p>
                            </div>
                          </div>
                          <button
                            onClick={() => navigate('/vehicle-contracts/analytics')}
                            className="px-3 py-1 text-sm bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors"
                          >
                            View Details
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Total Contracts</span>
                            <span className="font-bold text-gray-800">{vehicleContractData.summary.total_contracts ?? 0}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Paid</span>
                            <span className="font-bold text-green-600">{formatCurrency(vehicleContractData.summary.total_paid)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                            <span className="text-sm text-gray-600">Balance</span>
                            <span className="font-bold text-orange-600">{formatCurrency(vehicleContractData.summary.total_balance)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Inventory & Expense Overview */}
                {!isAgent && hasExpensesAccess && dashboardData?.inventory && dashboardData?.expenses && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl shadow-md p-4 lg:p-6">
                      <div className="flex items-center mb-4">
                        <div className="p-2.5 rounded-xl bg-rose-100 text-rose-600 border border-rose-200 mr-3">
                          <ShoppingCart className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800">Expense Overview</h3>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Total Expenses:</span>
                          <span className="text-lg font-bold text-gray-800">{formatCurrency(dashboardData.expenses.total_expenses)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Paid:</span>
                          <span className="text-sm font-semibold text-green-600">{formatCurrency(dashboardData.expenses.paid_expenses)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Pending:</span>
                          <span className="text-sm font-semibold text-orange-600">{formatCurrency(dashboardData.expenses.pending_expenses)}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Top Categories:</span>
                          <div className="mt-2 space-y-1">
                            {Object.entries(dashboardData.expenses.by_category).slice(0, 3).map(([cat, amount]) => (
                              <div key={cat} className="flex justify-between text-xs">
                                <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                                <span className="font-semibold">{formatCurrency(amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Module Cards — same company can_access rules as Reports.js */}
                <div className="mb-6">
                  <h2 className="text-base md:text-xl font-bold text-gray-800 mb-3 md:mb-4">Detailed Analytics</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {hasShipmentAccess && (
                      <ModuleCard
                        title="Shipment Analytics"
                        description="Detailed shipment data by status, user, vehicle, payment mode, and location"
                        icon={Package}
                        color="bg-blue-100 text-blue-600 border-blue-200"
                        accent="bg-blue-200"
                        onClick={() => navigate('/analytics/shipments')}
                      />
                    )}
                    {!isAgent && hasShipmentAccess && (
                      <ModuleCard
                        title="User Performance"
                        description="Agent and employee performance metrics with collection rates"
                        icon={Users}
                        color="bg-indigo-100 text-indigo-600 border-indigo-200"
                        accent="bg-indigo-200"
                        onClick={() => navigate('/analytics/user-performance')}
                      />
                    )}
                    {!isAgent && hasShipmentAccess && (
                      <ModuleCard
                        title="Vehicle Analytics"
                        description="Fleet utilization, revenue generation, and performance tracking"
                        icon={Truck}
                        color="bg-amber-100 text-amber-600 border-amber-200"
                        accent="bg-amber-200"
                        onClick={() => navigate('/analytics/vehicles')}
                      />
                    )}
                    {!isAgent && hasOnContractAccess && (
                      <ModuleCard
                        title="On Contract Analytics"
                        description="Track on-contract shipments, revenue, and contract performance metrics"
                        icon={FileText}
                        color="bg-cyan-100 text-cyan-600 border-cyan-200"
                        accent="bg-cyan-200"
                        onClick={() => navigate('/on-contracts/analytics')}
                      />
                    )}
                    {!isAgent && hasOnContractTruckAccess && (
                      <ModuleCard
                        title="Vehicle Contract Analytics"
                        description="Fleet contract management with KM limits, payments, and daily trips tracking"
                        icon={Truck}
                        color="bg-purple-100 text-purple-600 border-purple-200"
                        accent="bg-purple-200"
                        onClick={() => navigate('/vehicle-contracts/analytics')}
                      />
                    )}
                    {hasShipmentAccess && (
                      <ModuleCard
                        title="Missing & Returned"
                        description="Track missing shipments, returns, refunds, and financial impact"
                        icon={AlertCircle}
                        color="bg-rose-100 text-rose-600 border-rose-200"
                        accent="bg-rose-200"
                        onClick={() => navigate('/analytics/missing-returned')}
                      />
                    )}
                    {!isAgent && (hasShipmentAccess || hasExpensesAccess) && (
                      <ModuleCard
                        title="Profit & Loss Report"
                        description="Comprehensive P&L with revenue breakdown, expenses, and trends"
                        icon={TrendingUp}
                        color="bg-emerald-100 text-emerald-600 border-emerald-200"
                        accent="bg-emerald-200"
                        onClick={() => navigate('/analytics/profit-loss')}
                      />
                    )}
                    {!isAgent && hasCommissionAccess && (
                      <ModuleCard
                        title="Commission Analytics"
                        description={
                          hasExternalCommissionAccess(currentUser)
                            ? 'Track external shipment commission payments and payouts'
                            : 'ML agent commission by hub, branch, and agent with date filters'
                        }
                        icon={IndianRupee}
                        color="bg-orange-100 text-orange-600 border-orange-200"
                        accent="bg-orange-200"
                        onClick={() =>
                          navigate(
                            hasExternalCommissionAccess(currentUser)
                              ? '/external-commission-dashboard'
                              : '/commission-analytics',
                          )
                        }
                      />
                    )}
                    {showAgentNetworkTop && (
                      <ModuleCard
                        title="Agent Network Analytics"
                        description="Partnerships, external shipments by city, and partner agents"
                        icon={Network}
                        color="bg-violet-100 text-violet-600 border-violet-200"
                        accent="bg-violet-200"
                        onClick={() => navigate('/analytics/agent-network')}
                      />
                    )}
                  </div>
                </div>

                {/* Alerts Section */}
                {hasActiveAlerts && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-xl">
                    <div className="flex items-center mb-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                      <h3 className="font-semibold text-gray-800">Alerts & Notifications</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-sm">
                      {dashboardData.alerts.pending_salary_count > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Pending Salaries</span>
                          <span className="font-semibold text-orange-600">
                            {dashboardData.alerts.pending_salary_count} ({formatCurrency(dashboardData.alerts.pending_salary_amount)})
                          </span>
                        </div>
                      )}
                      {dashboardData.alerts.pending_expense_count > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Pending Expenses</span>
                          <span className="font-semibold text-orange-600">
                            {dashboardData.alerts.pending_expense_count} ({formatCurrency(dashboardData.alerts.pending_expense_amount)})
                          </span>
                        </div>
                      )}
                      {dashboardData.alerts.missing_shipments > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">{t('pages.missing.title')}</span>
                          <span className="font-semibold text-red-600">{dashboardData.alerts.missing_shipments}</span>
                        </div>
                      )}
                      {dashboardData.alerts.returned_shipments > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Returned Shipments</span>
                          <span className="font-semibold text-blue-600">{dashboardData.alerts.returned_shipments}</span>
                        </div>
                      )}
                      {dashboardData.alerts.inventory_alerts?.out_of_stock > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Out of Stock Items</span>
                          <span className="font-semibold text-red-600">{dashboardData.alerts.inventory_alerts.out_of_stock}</span>
                        </div>
                      )}
                      {dashboardData.alerts.inventory_alerts?.low_stock > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded">
                          <span className="text-gray-700">Low Stock Items</span>
                          <span className="font-semibold text-orange-600">{dashboardData.alerts.inventory_alerts.low_stock}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
    </DashboardLayout>
  );
};

export default Analytics;
